/**
 * Sources we plan to support but cannot connect yet. They appear on the
 * connectors page as disabled "coming soon" cards.
 *
 * Add a row here to announce a source — nothing else needs to change. A row
 * disappears from the page on its own once the server's catalog lists the
 * same key. `type` uses the same category keys as `data/connectors.ts`, plus
 * `ANALYTICS`, `DATABASE` and `APP`. `logoKey` names a brand asset in
 * `connectorIcons.ts`; without one the card uses the category's generic icon.
 */
export interface ComingSoonConnector {
  key: string;
  name: string;
  /** Vendor domain shown under the name. */
  domain: string;
  description: string;
  type:
    | "ANALYTICS"
    | "APP"
    | "BI"
    | "CRM"
    | "DATABASE"
    | "DATA_WAREHOUSE"
    | "FILES"
    | "PRODUCTIVITY";
  logoKey?: string;
}

export const comingSoonConnectors: readonly ComingSoonConnector[] = [
  {
    key: "segment",
    name: "Segment",
    domain: "segment.com",
    description:
      "Bring customer event and profile data from Segment into your knowledge graph.",
    type: "ANALYTICS",
  },
  {
    key: "heap",
    name: "Heap",
    domain: "heap.io",
    description:
      "Analyse product usage captured by Heap alongside the rest of your data.",
    type: "ANALYTICS",
  },
  {
    key: "posthog",
    name: "PostHog",
    domain: "posthog.com",
    description:
      "Connect PostHog events and feature-flag data for product analytics stories.",
    type: "ANALYTICS",
  },
  {
    key: "oracle",
    name: "Oracle Database",
    domain: "oracle.com",
    description:
      "Query Oracle Database tables and views directly from your stories.",
    type: "DATABASE",
  },
  {
    key: "mongodb",
    name: "MongoDB",
    domain: "mongodb.com",
    description:
      "Connect MongoDB collections and explore document data alongside your tables.",
    type: "DATABASE",
    logoKey: "mongodb",
  },
  {
    key: "mariadb",
    name: "MariaDB",
    domain: "mariadb.com",
    description: "Connect MariaDB databases and analyse their tables in place.",
    type: "DATABASE",
  },
  {
    key: "redis",
    name: "Redis",
    domain: "redis.io",
    description: "Read key-value data from Redis for real-time analysis.",
    type: "DATABASE",
    logoKey: "redis",
  },
  {
    key: "clickhouse",
    name: "ClickHouse",
    domain: "clickhouse.com",
    description:
      "Run fast analytical queries over ClickHouse tables at scale.",
    type: "DATA_WAREHOUSE",
  },
  {
    key: "synapse",
    name: "Azure Synapse Analytics",
    domain: "azure.microsoft.com",
    description:
      "Connect Azure Synapse Analytics to analyse warehouse data in your stories.",
    type: "DATA_WAREHOUSE",
  },
  {
    key: "teradata",
    name: "Teradata",
    domain: "teradata.com",
    description: "Connect Teradata Vantage and query enterprise warehouse data.",
    type: "DATA_WAREHOUSE",
  },
  {
    key: "pipedrive",
    name: "Pipedrive",
    domain: "pipedrive.com",
    description:
      "Sync deals, contacts and pipeline stages from Pipedrive for sales reporting.",
    type: "CRM",
  },
  {
    key: "zohocrm",
    name: "Zoho CRM",
    domain: "zoho.com",
    description:
      "Bring leads, accounts and deals from Zoho CRM into your analysis.",
    type: "CRM",
  },
  {
    key: "freshdesk",
    name: "Freshdesk",
    domain: "freshdesk.com",
    description:
      "Analyse support tickets and response times from Freshdesk.",
    type: "APP",
  },
  {
    key: "monday",
    name: "monday.com",
    domain: "monday.com",
    description:
      "Connect monday.com boards to report on projects and workloads.",
    type: "PRODUCTIVITY",
  },
  {
    key: "googleads",
    name: "Google Ads",
    domain: "ads.google.com",
    description:
      "Pull campaign spend and performance from Google Ads for marketing stories.",
    type: "APP",
  },
  {
    key: "metaads",
    name: "Meta Ads",
    domain: "facebook.com",
    description:
      "Pull campaign spend and results from Meta Ads for marketing stories.",
    type: "APP",
  },
  {
    key: "superset",
    name: "Apache Superset",
    domain: "superset.apache.org",
    description:
      "Connect Apache Superset dashboards and datasets to your knowledge graph.",
    type: "BI",
  },
  {
    key: "sigma",
    name: "Sigma",
    domain: "sigmacomputing.com",
    description:
      "Connect Sigma workbooks and datasets to your knowledge graph.",
    type: "BI",
  },
  {
    key: "mode",
    name: "Mode",
    domain: "mode.com",
    description: "Connect Mode reports and queries to your knowledge graph.",
    type: "BI",
  },
  {
    key: "domo",
    name: "Domo",
    domain: "domo.com",
    description: "Connect Domo cards and datasets to your knowledge graph.",
    type: "BI",
  },
  {
    key: "s3",
    name: "Amazon S3",
    domain: "aws.amazon.com",
    description:
      "Read CSV, Parquet and JSON files from Amazon S3 buckets.",
    type: "FILES",
    logoKey: "s3",
  },
  {
    key: "dropbox",
    name: "Dropbox",
    domain: "dropbox.com",
    description: "Connect spreadsheets and data files stored in Dropbox.",
    type: "FILES",
  },
];
